import { useState, useMemo, useEffect, useCallback } from 'react';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useBookings, useFacilityData } from '../../hooks/booking/useBookingContextSlices';
import { useBookingApprovals } from '../../hooks/booking/useBookingApprovals';
import { dashboardAPI } from '../../services/api';
import {
  getBookingCalendarDateStr,
  parseBookingTimeRange,
  formatHm,
  isUpcomingAfterToday,
} from './homeDashboardUtils';

export function useHomeDashboard() {
  const { bookings } = useBookings();
  const { facility } = useFacilityData();
  const { count: pendingRequestCount } = useBookingApprovals({ filter: 'pending', pollPending: true });
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  const dayName = format(today, 'EEEE');
  const dayNumber = format(today, 'd');

  const todaysBookings = useMemo(() => {
    return bookings
      .filter((booking) => {
        if (booking.status === 'cancelled') return false;
        return getBookingCalendarDateStr(booking) === todayStr;
      })
      .sort((a, b) => {
        const ra = parseBookingTimeRange(a);
        const rb = parseBookingTimeRange(b);
        const ma = ra ? ra.startH * 60 + ra.startM : 0;
        const mb = rb ? rb.startH * 60 + rb.startM : 0;
        return ma - mb;
      });
  }, [bookings, todayStr]);

  const totalSlotsToday = 20;
  const bookedSlotsToday = todaysBookings.length;
  const occupancyPercentage = Math.round((bookedSlotsToday / totalSlotsToday) * 100);

  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
  const bookingsThisWeek = bookings.filter((booking) => {
    if (booking.status === 'cancelled') return false;
    const bookingDate = new Date(booking.date || booking.bookingDate);
    return bookingDate >= weekStart && bookingDate <= weekEnd;
  }).length;

  const lastWeekStart = new Date(weekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(weekEnd);
  lastWeekEnd.setDate(lastWeekEnd.getDate() - 7);
  const bookingsLastWeek = bookings.filter((booking) => {
    if (booking.status === 'cancelled') return false;
    const bookingDate = new Date(booking.date || booking.bookingDate);
    return bookingDate >= lastWeekStart && bookingDate <= lastWeekEnd;
  }).length;

  const monthStart = startOfMonth(today);
  const monthEnd = endOfMonth(today);
  const revenueThisMonth = bookings
    .filter((booking) => {
      if (booking.status === 'cancelled') return false;
      const bookingDate = new Date(booking.bookingDate || booking.date);
      return bookingDate >= monthStart && bookingDate <= monthEnd;
    })
    .reduce((sum, booking) => sum + (parseFloat(booking.totalAmount) || parseFloat(booking.price) || 0), 0);

  const lastMonthStart = startOfMonth(subMonths(today, 1));
  const lastMonthEnd = endOfMonth(subMonths(today, 1));
  const revenueLastMonth = bookings
    .filter((booking) => {
      if (booking.status === 'cancelled') return false;
      const bookingDate = new Date(booking.bookingDate || booking.date);
      return bookingDate >= lastMonthStart && bookingDate <= lastMonthEnd;
    })
    .reduce((sum, booking) => sum + (parseFloat(booking.totalAmount) || parseFloat(booking.price) || 0), 0);
  const revenueDelta = revenueThisMonth - revenueLastMonth;

  // Future dates only — today's bookings already appear under "Today's schedule".
  const upcomingBookings = useMemo(() => {
    return bookings
      .filter((booking) => isUpcomingAfterToday(booking, todayStr))
      .sort((a, b) => {
        const ca = getBookingCalendarDateStr(a);
        const cb = getBookingCalendarDateStr(b);
        if (ca !== cb) return ca.localeCompare(cb);
        const ra = parseBookingTimeRange(a);
        const rb = parseBookingTimeRange(b);
        const ma = ra ? ra.startH * 60 + ra.startM : 0;
        const mb = rb ? rb.startH * 60 + rb.startM : 0;
        return ma - mb;
      })
      .slice(0, 8);
  }, [bookings, todayStr]);

  const upcomingFingerprintCounts = useMemo(() => {
    const counts = {};
    upcomingBookings.forEach((b) => {
      const customer = b.bookedBy || b.teamA?.captainName || b.contactName || 'Guest';
      const fp = [
        getBookingCalendarDateStr(b),
        b.timeSlot || '',
        b.serviceName || '',
        customer,
      ].join('\u0001');
      counts[fp] = (counts[fp] || 0) + 1;
    });
    return counts;
  }, [upcomingBookings]);

  // Per-date count of active (non-cancelled) bookings, keyed by yyyy-MM-dd.
  // Used to cross-reference the weather forecast for rain-impact alerts.
  const bookingCountByDate = useMemo(() => {
    const map = {};
    bookings.forEach((booking) => {
      if (booking.status === 'cancelled') return;
      const cal = getBookingCalendarDateStr(booking);
      if (!cal) return;
      map[cal] = (map[cal] || 0) + 1;
    });
    return map;
  }, [bookings]);

  const slotSummary = useMemo(() => {
    const summary = {};
    todaysBookings.forEach((booking) => {
      const serviceName = booking.serviceName || 'Unknown';
      if (!summary[serviceName]) {
        summary[serviceName] = { booked: 0, total: 10 };
      }
      summary[serviceName].booked += 1;
    });
    return summary;
  }, [todaysBookings]);

  const refresh = useCallback(() => {
    setSummaryLoading(true);
    setSummaryError(false);
    return dashboardAPI.getSummary()
      .then((res) => {
        if (res?.data) setDashboardSummary(res.data);
        setLastSyncedAt(new Date());
      })
      // Preserve the last good summary on a transient error so a failed manual
      // refresh doesn't wipe stats that are correctly on screen.
      .catch(() => setSummaryError(true))
      .finally(() => setSummaryLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const summary = dashboardSummary || {};
  const equipment = summary.equipment || {};
  const paymentsSummary = summary.payments || {};
  const operationsSummary = summary.operations || {};
  const supportSummary = summary.support || {};
  const inventorySummary = summary.inventory || {};
  const safeArray = (v) => (Array.isArray(v) ? v : []);
  const overdue = safeArray(equipment.overdue);
  const openTicketsCount = supportSummary.openCount ?? 0;
  const inventoryTotalItems = inventorySummary.totalItems ?? 0;
  const inventoryLowStockCount = inventorySummary.lowStockCount ?? 0;
  const totalOpsTasks = operationsSummary.totalTasks ?? 0;
  const completedOpsTasks = operationsSummary.completedCount ?? 0;

  const dynamicInsights = useMemo(() => {
    const valid = bookings.filter((b) => b.status !== 'cancelled');
    const byService = {};
    const bySlot = {};
    const rangeCounts = {};
    valid.forEach((b) => {
      const name = b.serviceName || 'Unknown';
      byService[name] = (byService[name] || 0) + 1;
      const slot = b.timeSlot || b.slotName || 'N/A';
      bySlot[slot] = (bySlot[slot] || 0) + 1;
      const tr = parseBookingTimeRange(b);
      if (tr) {
        const key = `${formatHm(tr.startH, tr.startM)}-${formatHm(tr.endH, tr.endM)}`;
        rangeCounts[key] = (rangeCounts[key] || 0) + 1;
      }
    });
    const topService = Object.entries(byService).sort((a, b) => b[1] - a[1])[0];
    const topSlot = Object.entries(bySlot).sort((a, b) => b[1] - a[1])[0];
    const topRange = Object.entries(rangeCounts).sort((a, b) => b[1] - a[1])[0];

    let peakBookingHours = '–';
    if (topRange) {
      const [startHm, endHm] = topRange[0].split('-');
      const dayTally = {};
      valid.forEach((b) => {
        const tr = parseBookingTimeRange(b);
        if (!tr) return;
        const key = `${formatHm(tr.startH, tr.startM)}-${formatHm(tr.endH, tr.endM)}`;
        if (key !== topRange[0]) return;
        const cd = getBookingCalendarDateStr(b);
        if (!cd) return;
        const d = parseISO(`${cd}T12:00:00`);
        if (Number.isNaN(d.getTime())) return;
        const label = format(d, 'EEE');
        dayTally[label] = (dayTally[label] || 0) + 1;
      });
      const topDay = Object.entries(dayTally).sort((a, b) => b[1] - a[1])[0]?.[0];
      const enDash = '\u2013';
      peakBookingHours = topDay
        ? `${topDay} ${startHm} ${enDash} ${endHm}`
        : `${startHm} ${enDash} ${endHm}`;
    }

    return {
      mostPopularService: topService?.[0] || '–',
      peakBookingHours,
      mostBookedSlot: topSlot?.[0] || '–',
      cancellationsToday: bookings.filter(
        (b) => b.status === 'cancelled' && getBookingCalendarDateStr(b) === todayStr
      ).length,
    };
  }, [bookings, todayStr]);

  return {
    facility,
    occupancyPercentage,
    bookedSlotsToday,
    totalSlotsToday,
    bookingsThisWeek,
    bookingsLastWeek,
    revenueThisMonth,
    revenueLastMonth,
    revenueDelta,
    pendingRequestCount,
    summaryLoading,
    summaryError,
    paymentsSummary,
    overdue,
    totalOpsTasks,
    completedOpsTasks,
    openTicketsCount,
    inventoryTotalItems,
    inventoryLowStockCount,
    todaysBookings,
    dayName,
    dayNumber,
    today,
    slotSummary,
    bookingCountByDate,
    upcomingBookings,
    upcomingFingerprintCounts,
    dynamicInsights,
    operationsSummary,
    lastSyncedAt,
    refresh,
  };
}
